import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import ServiceMeta from '@/components/ServiceMeta';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { DollarSign, Upload, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const AdSenseServicePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    address: '',
    country: '',
    email: ''
  });
  const [passportFile, setPassportFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!passportFile) {
      toast.error('Veuillez uploader votre passeport');
      return;
    }
    
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.full_name);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('passport', passportFile);
      
      await api.post('/service/adsense/submit', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSubmitted(true);
      toast.success('✅ Demande soumise ! Procédez au paiement');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pb-12">
        <ServiceMeta 
          title="Compte Google AdSense - Monétisation YouTube"
          description="Créez votre compte Google AdSense pour monétiser vos vidéos YouTube et générer des revenus publicitaires. Service professionnel à 150$."
          url="/creer-adsense"
        />
        <Header />
        
        <main className="pt-24 max-w-3xl mx-auto px-4">
          <Card className="glass border-zinc-800 text-center">
            <CardContent className="py-12">
              <CheckCircle className="mx-auto mb-4 text-green-500" size={64} />
              <h2 className="text-2xl font-bold text-white mb-3">
                Formulaire Validé ! 🎉
              </h2>
              <p className="text-zinc-400 mb-6">
                Procédez au paiement pour la création de votre compte Google AdSense.
              </p>
              <div className="bg-zinc-900 rounded-lg p-6 mb-6">
                <p className="text-3xl font-bold text-[#F97316] mb-3">150$</p>
                <Button className="w-full bg-orange-600 hover:bg-orange-700 py-6 text-lg">
                  PAYER 150$ MAINTENANT
                </Button>
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                Retour au Dashboard
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12">
      <ServiceMeta 
        title="Compte Google AdSense - Monétisation YouTube"
        description="Créez votre compte Google AdSense pour monétiser vos vidéos YouTube et générer des revenus publicitaires. Service professionnel à 150$."
        url="/creer-adsense"
      />
      <Header />
      
      <main className="pt-24 max-w-3xl mx-auto px-4">
        <Button 
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2" size={18} />
          Retour
        </Button>

        <Card className="glass border-zinc-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <DollarSign className="text-orange-500" size={32} />
              Créer Mon Google AdSense - 150$
            </CardTitle>
            <p className="text-zinc-400 text-sm mt-2">
              Étape 1 : Déposez votre dossier. Paiement ensuite.
            </p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label>Nom Complet (tel que sur le passeport) *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  required
                  placeholder="Ex: Jean Kabongo"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Adresse Complète *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                  placeholder="Ex: 123 Avenue de la Liberté, Kinshasa"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Pays *</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                  required
                  placeholder="Ex: RD Congo"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Email pour le compte AdSense *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  placeholder="Ex: moncompte@gmail.com"
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <div>
                <Label>Upload Passeport (JPG, PNG, PDF) *</Label>
                <div className="mt-2">
                  <label className="flex items-center justify-center w-full p-6 border-2 border-dashed border-zinc-700 rounded-lg cursor-pointer hover:border-orange-500 transition-colors bg-zinc-900">
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 text-zinc-500" size={32} />
                      <p className="text-sm text-zinc-400">
                        {passportFile ? passportFile.name : 'Cliquez pour uploader votre passeport'}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={(e) => setPassportFile(e.target.files[0])}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              </div>

              <div className="bg-orange-900/20 border border-orange-800 rounded-lg p-4 text-sm text-orange-200">
                <strong>💰 Ce que vous obtenez :</strong>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>Compte Google AdSense créé et vérifié</li>
                  <li>Identifiants envoyés par email/WhatsApp</li>
                  <li>Support pour activation</li>
                  <li>Monétisation YouTube activée</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-800 hover:opacity-90 py-6 text-lg font-semibold"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'CONTINUER VERS LE PAIEMENT'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdSenseServicePage;
